import { ref } from 'vue';
import { buildAllocationApiPayload } from 'dashboard/components-next/planning/Scheduler/allocationPayload';
import {
  ALLOCATION_TIME_SNAP_MINUTES,
  snapAllocationRange,
  snapAllocationTime,
} from 'dashboard/components-next/planning/Scheduler/allocationTimeSnap';
import { useStore } from 'vuex';

const DEFAULT_DURATION_MS = 60 * 60 * 1000;
const DEFAULT_DURATION_MINUTES = 60;
const SNAP_MS = ALLOCATION_TIME_SNAP_MINUTES * 60 * 1000;

export function childSlotDurationMs(parentDurationMs) {
  const half = Math.floor(parentDurationMs / 2 / SNAP_MS) * SNAP_MS;
  return Math.max(SNAP_MS, half);
}

function allocationDurationMs(allocation) {
  const start = new Date(allocation.start_at).getTime();
  const end = new Date(allocation.end_at).getTime();
  const duration = end - start;
  return duration > 0 ? duration : DEFAULT_DURATION_MS;
}

function roundUpToMinutes(date, minutes = ALLOCATION_TIME_SNAP_MINUTES) {
  const ms = minutes * 60 * 1000;
  return new Date(Math.ceil(date.getTime() / ms) * ms);
}

function resourceIdForSlot(spaces, slot) {
  const space = (spaces || []).find(
    s => s.id === slot.space_id || s.resource_id === slot.space_id
  );
  return space?.resource_id || slot.resource_id || null;
}

function pickRandomResourceId(spaces, resources) {
  const spacesWithResource = (spaces || []).filter(space => space.resource_id);
  if (spacesWithResource.length > 0) {
    const pick =
      spacesWithResource[Math.floor(Math.random() * spacesWithResource.length)];
    return pick.resource_id;
  }

  const availableResources = (resources || []).filter(resource => resource.id);
  if (availableResources.length > 0) {
    return availableResources[
      Math.floor(Math.random() * availableResources.length)
    ].id;
  }

  return null;
}

function buildFallbackFutureSlot(resourceId, durationMs = DEFAULT_DURATION_MS) {
  const startAt = roundUpToMinutes(new Date(Date.now() + 60_000));
  const snapped = snapAllocationRange({
    start_at: startAt,
    end_at: new Date(startAt.getTime() + durationMs),
  });
  return {
    resource_id: resourceId,
    start_at: snapped.start_at,
    end_at: snapped.end_at,
    buffer_before_min: 0,
    buffer_after_min: 0,
    category: null,
  };
}

async function findNextAvailableSlot(
  store,
  spaces,
  {
    durationMinutes = DEFAULT_DURATION_MINUTES,
    preferredResourceId = null,
  } = {}
) {
  const spaceIds = (spaces || []).map(space => space.id).filter(Boolean);
  if (!spaceIds.length) return null;

  try {
    const slots = await store.dispatch(
      'planningAllocations/findAvailableSlots',
      {
        space_ids: spaceIds,
        duration_minutes: durationMinutes,
        max_results: 20,
      }
    );

    const now = Date.now();
    let candidates = (slots || []).filter(
      slot => new Date(slot.start_at).getTime() > now
    );

    if (preferredResourceId) {
      const preferred = candidates.filter(
        slot => resourceIdForSlot(spaces, slot) === preferredResourceId
      );
      if (preferred.length) candidates = preferred;
    }

    if (!candidates.length) return null;

    const pick = candidates[Math.floor(Math.random() * candidates.length)];
    const resourceId = resourceIdForSlot(spaces, pick);
    if (!resourceId) return null;

    return {
      resource_id: resourceId,
      start_at: new Date(pick.start_at),
      end_at: new Date(pick.end_at),
      buffer_before_min: 0,
      buffer_after_min: 0,
      category: null,
    };
  } catch {
    return null;
  }
}

function buildSlotDefaults(referenceAllocation, nextAllocation = null) {
  const startAt = snapAllocationTime(referenceAllocation.end_at, 'ceil');
  const parentDurationMs = allocationDurationMs(referenceAllocation);
  let endAt = new Date(
    startAt.getTime() + childSlotDurationMs(parentDurationMs)
  );

  if (nextAllocation) {
    const nextStart = snapAllocationTime(nextAllocation.start_at, 'floor');
    if (endAt > nextStart && nextStart > startAt) {
      endAt = nextStart;
    }
  }

  const snapped = snapAllocationRange(
    { start_at: startAt, end_at: endAt },
    { startMode: 'ceil', endMode: 'ceil' }
  );

  if (snapped.end_at <= snapped.start_at) {
    snapped.end_at = new Date(snapped.start_at.getTime() + SNAP_MS);
  }

  return {
    resource_id: referenceAllocation.resource_id,
    start_at: snapped.start_at,
    end_at: snapped.end_at,
    buffer_before_min: 0,
    buffer_after_min: 0,
    category: referenceAllocation.category || null,
  };
}

async function resolveSlotDefaults(
  store,
  spaces,
  resources,
  { referenceAllocation = null, nextAllocation = null } = {}
) {
  if (referenceAllocation) {
    return buildSlotDefaults(referenceAllocation, nextAllocation);
  }

  const available = await findNextAvailableSlot(store, spaces, {
    durationMinutes: DEFAULT_DURATION_MINUTES,
  });
  if (available) return available;

  return buildFallbackFutureSlot(pickRandomResourceId(spaces, resources));
}

export function useAddEventSlot({ eventId, event, spaces, resources }) {
  const store = useStore();
  const addingSlot = ref(false);

  const createSlot = async (slotData, { title } = {}) => {
    if (!slotData?.resource_id || !eventId.value) return;

    await store.dispatch(
      'planningAllocations/create',
      buildAllocationApiPayload(slotData, {
        title,
        event_id: eventId.value,
        parent_allocation_id: null,
      })
    );
  };

  const addSlotAfter = async (referenceAllocation, nextAllocation = null) => {
    if (!eventId.value || addingSlot.value) return;

    addingSlot.value = true;
    try {
      const slotData = await resolveSlotDefaults(
        store,
        spaces.value,
        resources.value,
        { referenceAllocation, nextAllocation }
      );
      if (!slotData) return;

      const title = referenceAllocation.title || event.value?.name;
      await createSlot(slotData, { title });
      event.value = await store.dispatch('planningEvents/show', eventId.value);
    } finally {
      addingSlot.value = false;
    }
  };

  const addFirstSlot = async () => {
    if (!eventId.value || addingSlot.value) return;

    addingSlot.value = true;
    try {
      const slotData = await resolveSlotDefaults(
        store,
        spaces.value,
        resources.value
      );
      if (!slotData) return;

      await createSlot(slotData, { title: event.value?.name });
      event.value = await store.dispatch('planningEvents/show', eventId.value);
    } finally {
      addingSlot.value = false;
    }
  };

  const duplicateSlot = async allocation => {
    if (!eventId.value || addingSlot.value || !allocation) return;

    addingSlot.value = true;
    try {
      const durationMs = allocationDurationMs(allocation);
      const slotData = buildSlotDefaults(allocation);
      slotData.end_at = new Date(slotData.start_at.getTime() + durationMs);

      const snapped = snapAllocationRange(
        { start_at: slotData.start_at, end_at: slotData.end_at },
        { startMode: 'ceil', endMode: 'ceil' }
      );
      if (snapped.end_at <= snapped.start_at) {
        snapped.end_at = new Date(snapped.start_at.getTime() + SNAP_MS);
      }

      await createSlot(
        {
          resource_id: allocation.resource_id || slotData.resource_id,
          start_at: snapped.start_at,
          end_at: snapped.end_at,
          buffer_before_min: allocation.buffer_before_min || 0,
          buffer_after_min: allocation.buffer_after_min || 0,
          category: allocation.category || null,
          description: allocation.description,
          metadata: allocation.metadata
            ? { ...allocation.metadata }
            : undefined,
        },
        { title: allocation.title || event.value?.name }
      );
      event.value = await store.dispatch('planningEvents/show', eventId.value);
    } finally {
      addingSlot.value = false;
    }
  };

  const saveTimeslot = async (form, existingAllocation = null) => {
    let resourceIds = [];
    if (Array.isArray(form?.resource_ids) && form.resource_ids.length) {
      resourceIds = form.resource_ids;
    } else if (form?.resource_id) {
      resourceIds = [form.resource_id];
    }
    resourceIds = resourceIds.filter(Boolean);

    if (!eventId.value || addingSlot.value || !resourceIds.length) return;

    addingSlot.value = true;
    try {
      if (existingAllocation) {
        await store.dispatch('planningAllocations/update', {
          id: existingAllocation.id,
          resource_id: resourceIds[0],
          start_at: form.start_at,
          end_at: form.end_at,
          timeZone: form.timeZone,
          title: form.title,
          description: form.comment || '',
          category: form.category || '',
          metadata: form.metadata,
        });
      } else {
        const title = form.title || event.value?.name;
        const sharedSlot = {
          start_at: form.start_at,
          end_at: form.end_at,
          timeZone: form.timeZone,
          description: form.comment || undefined,
          category: form.category || undefined,
          metadata: form.metadata,
        };

        await Promise.all(
          resourceIds.map(resourceId =>
            createSlot({ ...sharedSlot, resource_id: resourceId }, { title })
          )
        );
      }

      event.value = await store.dispatch('planningEvents/show', eventId.value);
    } finally {
      addingSlot.value = false;
    }
  };

  return {
    addingSlot,
    addSlotAfter,
    addFirstSlot,
    duplicateSlot,
    saveTimeslot,
  };
}
